import {
  Alert,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconConfigureLine,
  IconInfoSolid,
  List,
  Modal,
  NumberInput,
  Pill,
  RadioInput,
  RadioInputGroup,
  Spinner,
  Text,
  TextInput,
  ToggleDetails,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import {
  ERROR,
  useNotification,
} from "../components/CustomHooks/NotificationProvider";
import { CourseLevelService } from "../services/CourseLevelService";
import { sort } from "../utils/utils";
import { Card } from "./Card";
import { DialogYesNo } from "./DialogYesNo/DialogYesNo";
import { Progress } from "./Progress";
import { MULTI, Selector } from "./Selectors/Selector";

const CourseCodePattern = () => {
  const awardLevels = [
    { id: "conAdm", label: t("courseCodePattern.conditionalAdmission") },
    { id: "graduate", label: t("courseCodePattern.graduate") },
    { id: "remedial", label: t("courseCodePattern.remedial") },
    { id: "nonCredit", label: t("courseCodePattern.nonCredited") },
    { id: "undergrad", label: t("courseCodePattern.undergraduate") },
    { id: "other", label: t("courseCodePattern.other") },
  ];

  const showNotification = useNotification();
  const [loadingTriggered, setLoadingTriggered] = useState(false);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, isLoading] = useState(true);
  const [submitLoading, isSubmitLoading] = useState(false);
  const [prog, setProg] = useState(0);
  const [prevProg, setPrevProg] = useState(0);
  const [progNotUpdatedCount, setProgNotUpdatedCount] = useState(0);
  const [totalCourses, setTotalCourses] = useState(0);
  const [wasErrorUpdating, setWasErrorUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(true);
  const [numberOfCourseLevels, setNumberOfCourseLevels] = useState(2);
  const [courseLevels, setCourseLevels] = useState([]);
  const [numberOfDigits, setNumberOfDigits] = useState(0);
  const [hasSuffix, setHasSuffix] = useState(false);
  const [suffixType, setSuffixType] = useState("ignore");
  const [patternId, setPatternId] = useState(null);
  const [removeCourseDialogOpen, setRemoveCourseDialogOpen] = useState(false);
  const [courseIndexToRemove, setCourseIndexToRemove] = useState(null);
  const [trigger, setTrigger] = useState(true);
  const [errFlag, setErrFlag] = useState("");
  const [readOnlyFlag, setReadOnlyFlag] = useState(false);

  useEffect(() => {
    if (trigger) {
      setTrigger(false);
      isLoading(true);
      CourseLevelService.getCourseCodePattern().then((pattern) => {
        const patternVar = pattern[pattern.length - 1];
        if (pattern && pattern.length) {
          setPatternId(patternVar.id);
          setNumberOfCourseLevels(
            patternVar.courseLevels ? patternVar.courseLevels.length : 0
          );
          setNumberOfDigits(patternVar.numberOfCourseLevelDigits);
          // if there is a suffix in ccp
          setHasSuffix(true);
          setSuffixType(patternVar.suffix);
          if (patternVar.suffix !== "ignore") {
            setReadOnlyFlag(true);
          }

          const courseLevels = sort(
            [...patternVar.courseLevels],
            (level) => level.courseLevel
          );

          // Remove once range is implemented
          courseLevels.forEach((cl) => {
            if (cl.range) {
              cl.range = [cl.range];
            } else {
              cl.range = [{ start: "", end: "" }];
            }
          });

          setCourseLevels(courseLevels);
        }
        isLoading(false);
      });
    }
  }, [trigger]);

  useEffect(() => {
    if (loadingTriggered) {
      if (progNotUpdatedCount > 60) {
        // prog not updated in over 60 iterations of this useEffect
        setLoadingTriggered(false);
        setUpdateSuccess(false);
        setWasErrorUpdating(true);
        setPrevProg(0);
        setProgNotUpdatedCount(0);
      } else {
        setPrevProg(prog);
        setLoadingTriggered(false);
        setUpdateSuccess(false);
        CourseLevelService.getCourseLevelUpdateProgress()
          .then(async (progress) => {
            setModalOpen(true);
            setProg(progress[0]);
            setTotalCourses(progress[1]);
            if (prevProg === prog) {
              setProgNotUpdatedCount(
                (progNotUpdatedCount) => progNotUpdatedCount + 1
              );
            } else {
              setProgNotUpdatedCount(0);
            }
            // Currently there is no proper path to handle failed course code pattern updates
            // for preexisting course code patterns that did not match
            // Need to build
            if (progress[0] !== progress[1]) {
              await new Promise((resolve) => setTimeout(() => resolve(), 500));
              setLoadingTriggered(true);
            } else {
              // succeeded. reset vals and display success modal
              setUpdateSuccess(true);
              setPrevProg(0);
              setProgNotUpdatedCount(0);
              setProg(0);
              setTotalCourses(0);
            }
          })
          .catch((e) => {
            showNotification(
              `${t("notifications.courseCodePattern.patternUpdateError")} ${e}`,
              ERROR
            );
          });
      }
    }
  }, [
    loadingTriggered,
    prevProg,
    prog,
    progNotUpdatedCount,
    updateSuccess,
    showNotification,
  ]);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setErrFlag("");
    setTrigger(true);
  };

  // const showRemoveCourseConfirmation = (courseToRemove) => {
  //   setCourseIndexToRemove(courseToRemove);
  //   setRemoveCourseDialogOpen(true);
  // };

  const clickNoRemoveCourse = () => {
    setRemoveCourseDialogOpen(false);
  };

  const handleAddCourseLevel = (value) => {
    setNumberOfCourseLevels(value);
    courseLevels.push({
      seq: value - 1,
      title: "",
      courseLevel: "",
      awardLevelCategories: [],
      range: [{ start: "", end: "" }],
      regexPattern: "",
    });
  };

  const handleCourseDigitsChangeForLevel = () => {
    courseLevels.forEach((level) => {
      level.range[0].start = "";
      level.range[0].end = "";
    });
  };

  const handleRemoveCourseLevel = () => {
    const tempArr = courseLevels.filter(
      (item) => item !== courseLevels.splice(courseIndexToRemove, 1)
    );
    setCourseLevels(tempArr);
    setCourseIndexToRemove(null);
    setRemoveCourseDialogOpen(false);
  };
  const handleNumOfDigitsChange = (e) => {
    const maxLength = 6;
    const value = parseInt(e.currentTarget.value) || 0;
    if (value <= maxLength) {
      setNumberOfDigits(value);
      handleCourseDigitsChangeForLevel();
    } else {
      setNumberOfDigits(0);
      setErrFlag(`max number of digits is ${maxLength}`);
    }
  };

  const verifyRanges = () => {
    let invalid = "";
    let start = false;
    let end = false;
    const arr = [];
    courseLevels.forEach((level) => {
      if (arr.length > 0) {
        arr.forEach((range) => {
          if (
            parseInt(level.range[0].start) >= range.start &&
            parseInt(level.range[0].start) <= range.end
          ) {
            level.invalid = true;
            invalid = t("courseCodePattern.noOverlap");
          } else if (
            parseInt(level.range[0].start) < 0 ||
            parseInt(level.range[0].end) < 0
          ) {
            level.invalid = true;
            invalid = t("courseCodePattern.noNegative");
          } else if (
            (parseInt(level.range[0].end) >= range.start &&
              parseInt(level.range[0].end) <= range.end) ||
            (parseInt(level.range[0].start) <= range.start &&
              parseInt(level.range[0].end) >= range.end)
          ) {
            level.invalid = true;
            invalid = t("courseCodePattern.noOverlap");
          }
        });
      }
      level.range.forEach((range) => {
        if (!range.start || !range.end) {
          level.invalid = true;
          invalid = t("courseCodePattern.noUnbounded");
        } else if (parseInt(range.start) > parseInt(range.end)) {
          level.invalid = true;
          invalid = t("courseCodePattern.smallerLowerBound");
        } else if (range.start.length !== range.end.length) {
          level.invalid = true;
          invalid = t("courseCodePattern.digitCountsMustMatch");
        } else {
          level.invalid = false;
        }
        start = parseInt(range.start);
        end = parseInt(range.end);
        const obj = { start: start, end: end };
        arr.push(obj);
      });
    });
    setCourseLevels([...courseLevels]);
    return invalid;
  };

  const handleSubmit = () => {
    isSubmitLoading(true);
    reorderCourseLevels();
    const validity = verifyRanges();
    if (validity.length > 0) {
      setErrFlag(validity);
      isSubmitLoading(false);
    } else {
      CourseLevelService.createCourseCodePattern({
        id: patternId,
        numberOfCourseLevelDigits: numberOfDigits,
        suffix: suffixType,
        courseLevels: courseLevels,
      }).then(() => {
        setLoadingTriggered(true);
        setErrFlag("");
        handleClose();
        isSubmitLoading(false);
      });
    }
  };

  const reorderCourseLevels = () => {
    courseLevels.forEach((level, index) => {
      level.seq = index;
    });
    setCourseLevels([...courseLevels]);
  };

  const handleSuffixToggle = () => {
    const suffixToggle = !hasSuffix;
    const defaultSuffix = "ignore";

    setHasSuffix(suffixToggle);
    if (!suffixToggle) {
      setSuffixType(defaultSuffix);
    } else {
      setSuffixType(suffixType);
    }
  };

  const handleSuffixType = (event, value) => {
    setSuffixType(value);
  };

  // multiple ranges not supported by backend yet
  // const handleAddRange = (index) => {
  //   let newRange = { start: "", end: "" };
  //   courseLevels[index].range.push(newRange);
  //   setCourseLevels([...courseLevels]);
  // };

  // const handleRemoveRange = (levelIndex, rangeIndex) => {
  //   courseLevels[levelIndex].range.splice(rangeIndex, 1);
  //   setCourseLevels([...courseLevels]);
  // };

  return (
    <>
      <Grid hAlign="end" vAlign="middle">
        <Grid.Row>
          <Grid.Col width="auto">
            <Button
              color="primary"
              onClick={handleOpen}
              renderIcon={IconConfigureLine}
            >
              {t("courseCodePattern.createCoursePattern")}
            </Button>
          </Grid.Col>
        </Grid.Row>
      </Grid>

      <Modal
        open={open}
        onDismiss={handleClose}
        size="large"
        label={t("courseCodePattern.createCoursePattern")}
        shouldCloseOnDocumentClick
      >
        <Modal.Header>
          <Grid hAlign="space-between">
            <Grid.Row>
              <Grid.Col>
                <Heading level="h2" as="h1">
                  {t("courseCodePattern.createCoursePattern")}
                </Heading>
              </Grid.Col>
              <Grid.Col width="auto">
                <CloseButton
                  size="small"
                  screenReaderLabel={t("global.close")}
                  onClick={handleClose}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Modal.Header>
        <Modal.Body>
          <Flex justifyItems="space-between" alignItems="start" wrap="wrap">
            <Flex.Item>
              <NumberInput
                showArrows={false}
                renderLabel={
                  <Text size="small">
                    {t("courseCodePattern.courseLevelDigits")}
                    <Tooltip
                      renderTip={
                        <div style={{ width: 600 }}>
                          {t("courseCodePattern.digitInfo")}
                        </div>
                      }
                      on={["hover", "focus"]}
                      color="primary-inverse"
                      placement="end"
                    >
                      <span style={{ marginLeft: "5px" }}>
                        <IconInfoSolid color="brand" />
                      </span>
                    </Tooltip>
                  </Text>
                }
                display={"inline-block"}
                value={numberOfDigits}
                onChange={handleNumOfDigitsChange}
              />
            </Flex.Item>
            <Flex.Item size="small">
              {courseLevels && courseLevels.length > 0 && (
                <Checkbox
                  label={t("courseCodePattern.courseLevelHasSuffix")}
                  variant="toggle"
                  size="small"
                  checked={hasSuffix}
                  onChange={handleSuffixToggle}
                />
              )}
            </Flex.Item>
            <Flex.Item size="30%">
              {hasSuffix && (
                <RadioInputGroup
                  onChange={handleSuffixType}
                  name="suffixType"
                  size="small"
                  defaultValue={suffixType ? suffixType : "ignore"}
                  disabled={!!readOnlyFlag}
                  description={
                    <Text size="small">
                      {t("courseCodePattern.suffixSelectType")}
                    </Text>
                  }
                >
                  <RadioInput
                    value={t("courseCodePattern.suffixIgnoreInput")}
                    label="Ignore"
                  />
                  <RadioInput
                    value={t("courseCodePattern.suffixTopicInput")}
                    label="Topic"
                  />
                  <RadioInput
                    value={t("courseCodePattern.suffixRequisiteInput")}
                    label="Requisite"
                  />
                </RadioInputGroup>
              )}
            </Flex.Item>
            {errFlag.length > 0 && (
              <Flex.Item size="100%">
                <Alert variant="warning">{errFlag}</Alert>
              </Flex.Item>
            )}
            <Flex.Item width="100%" margin="small 0 0 0">
              <ToggleDetails
                size="small"
                summary={t("courseCodePattern.rangeExamples")}
              >
                <List>
                  <List.Item>
                    <Text size="small">
                      {t("courseCodePattern.coursecodeExample_1")}
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text size="small">
                      {t("courseCodePattern.coursecodeExample_2")}
                    </Text>
                  </List.Item>
                </List>
              </ToggleDetails>
            </Flex.Item>
          </Flex>

          <View as="div" margin="large 0 0">
            {courseLevels &&
              courseLevels.length > 0 &&
              courseLevels.map((course, index) => (
                <Flex
                  margin="0 0 medium 0"
                  justifyItems="space-between"
                  alignItems="start"
                  wrap="wrap"
                  key={index}
                >
                  <Flex.Item padding="0 medium 0 0" size="15%">
                    <TextInput
                      size="small"
                      renderLabel={
                        <Text size="medium">
                          {t("courseCodePattern.courseLevel")}
                        </Text>
                      }
                      onChange={(e) => {
                        course.courseLevel = e.target.value.replace(/\D/g, "");
                        setCourseLevels([...courseLevels]);
                      }}
                      value={course.courseLevel}
                    />
                  </Flex.Item>
                  <Flex.Item padding="0 medium 0 0" size="25%">
                    {course.range &&
                      course.range.map((range, ind) => (
                        <Flex alignItems="end" key={ind}>
                          <Flex.Item shouldGrow shouldShrink>
                            {ind === 0 && (
                              <Text size="medium" weight="bold">
                                {t("courseCodePattern.courseLevelRangeModal")}
                              </Text>
                            )}
                            <Flex
                              justifyItems="space-between"
                              alignItems="center"
                              margin={ind === 0 ? "small 0" : "0 0 small 0"}
                            >
                              <Flex.Item shouldShrink>
                                <TextInput
                                  aria-label={t("courseCodePattern.rangeStart")}
                                  size="small"
                                  value={range.start}
                                  messages={
                                    course.invalid
                                      ? [
                                          {
                                            text: t(
                                              "courseCodePattern.invalidRange"
                                            ),
                                            type: "error",
                                          },
                                        ]
                                      : []
                                  }
                                  onChange={(e) => {
                                    if (
                                      e.target.value.length <= numberOfDigits
                                    ) {
                                      range.start = e.target.value;
                                      setCourseLevels([...courseLevels]);
                                    }
                                  }}
                                />
                              </Flex.Item>
                              <Flex.Item padding="0 small">-</Flex.Item>
                              <Flex.Item shouldShrink>
                                <TextInput
                                  aria-label={t("courseCodePattern.rangeEnd")}
                                  size="small"
                                  interaction={
                                    range.start ? "enabled" : "disabled"
                                  }
                                  messages={
                                    course.invalid
                                      ? [
                                          {
                                            text: t(
                                              "courseCodePattern.invalidRange"
                                            ),
                                            type: "error",
                                          },
                                        ]
                                      : []
                                  }
                                  value={range.end}
                                  onChange={(e) => {
                                    if (
                                      e.target.value.length <= numberOfDigits
                                    ) {
                                      range.end = e.target.value;
                                      setCourseLevels([...courseLevels]);
                                    }
                                  }}
                                />
                              </Flex.Item>
                              {/* {ind === 0 ? (
                                    <Flex.Item padding="0 0 0 small">
                                      <IconButton
                                        size="small"
                                        screenReaderLabel="Add level range"
                                        onClick={() => handleAddRange(index)}
                                      >
                                        <IconAddLine />
                                      </IconButton>
                                    </Flex.Item>
                                  ) : (
                                    <Flex.Item padding="0 0 0 small">
                                      <IconButton
                                        size="small"
                                        screenReaderLabel="Add level range"
                                        onClick={() =>
                                          handleRemoveRange(index, ind)
                                        }
                                      >
                                        <IconXLine />
                                      </IconButton>
                                    </Flex.Item> */}
                              {/* )} */}
                            </Flex>
                          </Flex.Item>
                        </Flex>
                      ))}
                  </Flex.Item>
                  <Flex.Item
                    padding="0 medium 0 0"
                    shouldGrow
                    shouldShrink
                    size="50%"
                  >
                    <Selector
                      type={MULTI}
                      id={t("courseCodePattern.awardLevelId", { index: index })}
                      label={t("courseCodePattern.awardLevelTitle")}
                      options={awardLevels}
                      selectHandler={(value) => {
                        if (courseLevels[index]) {
                          courseLevels[index].awardLevelCategories = value;
                          setCourseLevels([...courseLevels]);
                        }
                      }}
                      selectedOptions={
                        courseLevels[index]
                          ? courseLevels[index].awardLevelCategories
                          : []
                      }
                      size="small"
                      assistiveText={t(
                        "courseCodePattern.awardLevelAssistedText"
                      )}
                    />
                  </Flex.Item>
                  <Flex.Item>
                    {/*
                        Currently we are not supporting deletion of course levels due to the complexity of the logic
                        of handling the reordering of the course levels. This may be implemented in the future.
                      */}
                    {/* <div style={{ marginTop: 30 }}>
                        <IconButton
                          size="small"
                          style={{ lineHeight: 0 }}
                          onClick={() => showRemoveCourseConfirmation(index)}
                          renderIcon={IconTrashLine}
                          screenReaderLabel={t(
                            "courseCodePattern.removePattern"
                          )}
                        />
                      </div> */}
                  </Flex.Item>
                </Flex>
              ))}
            <Button
              size="small"
              onClick={() => handleAddCourseLevel(numberOfCourseLevels + 1)}
              renderIcon={IconAddLine}
            >
              {t("courseCodePattern.addLevel")}
            </Button>
          </View>
        </Modal.Body>
        <Modal.Footer>
          <Button margin="0 small 0 0" onClick={handleClose}>
            {t("global.cancel")}
          </Button>

          <Button
            color="primary"
            type="submit"
            onClick={handleSubmit}
            disabled={submitLoading}
          >
            {submitLoading ? (
              <Spinner renderTitle="Please Wait" size="x-small" />
            ) : (
              t("global.submit")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <DialogYesNo
        message={t("courseCodePattern.removeCourseLevel")}
        openStatus={removeCourseDialogOpen}
        onClickNo={clickNoRemoveCourse}
        onClickYes={handleRemoveCourseLevel}
      />
      <View as="div" margin="small 0 small 0">
        <Grid hAlign="space-between" startAt="large">
          <Grid.Row>
            <Grid.Col>
              {loading ? (
                <Skeleton count={3} style={{ marginBottom: 10, height: 98 }} />
              ) : (
                <List margin="0" isUnstyled>
                  {courseLevels &&
                    courseLevels.map((level, index) => (
                      <List.Item key={index}>
                        <Card
                          style={{
                            color: "#0D323F",
                            backgroundColor: "#F2F8FA",
                            border: "1px solid #333",
                            borderRadius: "10px",
                            padding: "5px 15px",
                            marginBottom: 10,
                          }}
                        >
                          <Heading as="h3" level="h4">
                            {t("courseCodePattern.courseLevel")}{" "}
                            {level.courseLevel}
                          </Heading>
                          <Flex margin="small 0 0 0">
                            <Flex.Item>
                              <Text size="small" as="span">
                                {t("courseCodePattern.courseLevelRange")}
                              </Text>
                            </Flex.Item>
                            <Flex.Item margin="0 0 0 small">
                              <Text size="small">
                                {level.range[0].start} - {level.range[0].end}
                              </Text>
                            </Flex.Item>
                          </Flex>
                          {level.suffix === "ignore" && (
                            <Text size="small">
                              {t("courseCodePattern.ignoredSuffix")}
                            </Text>
                          )}
                          {level.suffix === "topic" && (
                            <Text size="small">
                              {t("courseCodePattern.suffixIsTopic")}
                            </Text>
                          )}
                          {level.suffix === "requisite" && (
                            <Text size="small">
                              {t("courseCodePattern.suffixIsRequisite")}
                            </Text>
                          )}
                          <Text size="small">
                            {t("courseCodePattern.awardLevelCategories")}
                            <span style={{ textTransform: "capitalize" }}>
                              {level.awardLevelCategories.map((level) => (
                                <Pill color="info" margin="x-small" key={level}>
                                  {
                                    awardLevels.find(
                                      (award) => award.id === level
                                    ).label
                                  }
                                </Pill>
                              ))}
                            </span>
                          </Text>
                        </Card>
                      </List.Item>
                    ))}
                </List>
              )}
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </View>
      <Progress
        success={updateSuccess}
        setParentProg={setProg}
        setTotalOutcomes={setTotalCourses}
        setModalOpen={setModalOpen}
        setShowErrorMessage={setWasErrorUpdating}
        setLoadingTriggered={setLoadingTriggered}
        loadingTriggered={loadingTriggered}
        showErrorMessage={wasErrorUpdating}
        currentProgress={prog}
        totalItems={totalCourses}
        item="courses"
        modalOpen={modalOpen}
        label="Course Code Pattern Update"
        successText={t("progress.newCourseCodePattern")}
        errorText={t("progress.courseCodePatternError")}
      />
    </>
  );
};
export default CourseCodePattern;
